import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Database,
  Loader2,
  ScanLine,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import type {
  SourceIngestGuidance,
  SourcePurpose,
} from './source-ingest-model'
import { getSourceIngestCopy } from './source-ingest-copy'

interface SourceIngestGuidancePanelProps {
  locale: string
  guidance: SourceIngestGuidance
  selectedPurpose: SourcePurpose
  statusMessage?: string | null
  technicalDetail?: string | null
  onPurposeChange: (purpose: SourcePurpose) => void
  onRecovery?: () => void
}

const purposeOrder: SourcePurpose[] = [
  'auto',
  'design-reference',
  'content-data',
  'requirements',
]

function PhaseIcon({ phase }: { phase: SourceIngestGuidance['phase'] }) {
  if (phase === 'ready') return <CheckCircle2 className="h-4 w-4" aria-hidden />
  if (phase === 'error' || phase === 'needs-action') {
    return <AlertTriangle className="h-4 w-4" aria-hidden />
  }
  if (phase === 'extracting' || phase === 'analyzing') {
    return <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
  }
  if (phase === 'detected') return <ScanLine className="h-4 w-4" aria-hidden />
  return <CircleDashed className="h-4 w-4" aria-hidden />
}

export function SourceIngestGuidancePanel({
  locale,
  guidance,
  selectedPurpose,
  statusMessage,
  technicalDetail,
  onPurposeChange,
  onRecovery,
}: SourceIngestGuidancePanelProps) {
  const copy = getSourceIngestCopy(locale)
  const alert = guidance.phase === 'error' || guidance.phase === 'needs-action'

  return (
    <section
      className="border-t border-border bg-card"
      aria-labelledby="source-ingest-status-title"
      data-testid="studio-source-ingest-guidance"
    >
      <div className="grid gap-0 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="border-b border-border p-5 lg:border-b-0 lg:border-r">
          <div className="flex items-start gap-3">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${alert ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground'}`}>
              <PhaseIcon phase={guidance.phase} />
            </span>
            <div className="min-w-0 flex-1">
              <div id="source-ingest-status-title" className="text-xs font-semibold text-muted-foreground">
                {copy.status}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-foreground" role="status" aria-live="polite">
                  {copy.phase[guidance.phase]}
                </span>
                <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {copy.detectedAs}: {copy.sourceKind[guidance.sourceKind]}
                </span>
              </div>
              {statusMessage ? (
                <p className="mt-2 text-xs leading-5 text-muted-foreground" role={alert ? 'alert' : undefined}>
                  {statusMessage}
                </p>
              ) : null}
              <div className="mt-4 text-xs font-semibold text-muted-foreground">
                {copy.purpose}
              </div>
              <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={copy.purpose}>
                {purposeOrder.map((purpose) => (
                  <button
                    key={purpose}
                    type="button"
                    aria-pressed={selectedPurpose === purpose}
                    onClick={() => onPurposeChange(purpose)}
                    className={`min-h-9 rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selectedPurpose === purpose ? 'border-foreground bg-foreground text-background' : 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  >
                    {copy.purposeOptions[purpose]}
                    {purpose === 'auto' ? ` · ${copy.purposeOptions[guidance.suggestedPurpose]}` : ''}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <EvidenceItem
              icon={Sparkles}
              label={copy.provenance}
              value={
                guidance.provenanceReady
                  ? copy.provenanceValue[guidance.provenanceKey]
                  : copy.provenancePending
              }
            />
            <EvidenceItem icon={ShieldCheck} label={copy.authority} value={copy.authorityValue[guidance.authorityKey]} />
            <EvidenceItem icon={Database} label={copy.usage} value={copy.usageValue[guidance.usageKey]} />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background px-3 py-3">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{copy.next}</div>
              <p className="mt-1 text-xs leading-5 text-foreground">{copy.nextValue[guidance.nextActionKey]}</p>
            </div>
            {guidance.recoveryAction && onRecovery ? (
              <button
                type="button"
                onClick={onRecovery}
                className="inline-flex min-h-10 items-center rounded-lg bg-foreground px-3 text-xs font-semibold text-background transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {copy.recovery[guidance.recoveryAction]}
              </button>
            ) : null}
          </div>
          {technicalDetail ? (
            <details className="mt-3 text-xs text-muted-foreground">
              <summary className="cursor-pointer font-medium text-foreground">{copy.technicalDetails}</summary>
              <p className="mt-2 break-words font-mono text-[11px] leading-5">{technicalDetail}</p>
            </details>
          ) : null}
        </div>
      </div>
    </section>
  )
}

function EvidenceItem({ icon: Icon, label, value }: { icon: typeof Sparkles; label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-xl border border-border bg-background p-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </div>
      <p className="mt-2 text-xs leading-5 text-foreground">{value}</p>
    </div>
  )
}
